import { useCreateEvent } from '@/features/events/hooks/useCreateEvent';
import { EventType } from '@/features/events/types/EventType';
import { CategoryHealthTrend } from '@/features/ocr/components/CategoryHealthTrend';
import { OverviewCard } from '@/features/ocr/components/OverviewCard';
import { RiskHealthTrend } from '@/features/ocr/components/RiskHealthTrend';
import { useGetParameters } from '@/features/ocr/hooks/useGetParameters';
import {
  ParameterData,
  parameterStatusUtils,
} from '@/features/ocr/types/ParameterData';
import { LoadingOverlay } from '@/features/shared/components/Loaders';
import { useEffect, useMemo, useState } from 'react';
import { FilterBar } from '../components/FilterBar';

// TODO [TM]: Metti i pallini nella stessa riga
const Parameters = () => {
  const [isFilteringCategory, setIsFilteringCategory] = useState(false);
  const { data, isLoading, isError } = useGetParameters();
  const { sendEvent } = useCreateEvent()

  // Helper function to get the most recent parameter for each name
  const getMostRecentParameters = (parameters: ParameterData[]) => {
    const groupedParams = parameters.reduce((acc, param) => {
      if (!acc[param.name]) {
        acc[param.name] = [];
      }
      acc[param.name].push(param);
      return acc;
    }, {} as Record<string, ParameterData[]>);

    return Object.values(groupedParams).map((params: ParameterData[]) =>
      params.reduce((latest: ParameterData, current: ParameterData) =>
        new Date(current.date) > new Date(latest.date) ? current : latest,
      ),
    );
  };

  // Get aggregated parameters
  const aggregatedParameters = useMemo(
    () => (data ? getMostRecentParameters(data) : []),
    [data],
  );

  const numberOfMarkers = aggregatedParameters.length;
  const normalMarkers = aggregatedParameters.filter(
    (parameter) =>
      parameterStatusUtils.simplifyStatus(
        parameterStatusUtils.calculateStatus(
          parameter.value,
          parameter.range.min,
          parameter.range.max,
          parameter.date,
        ).status,
      ) === 'normal',
  ).length;
  const warningMarkers = aggregatedParameters.filter(
    (parameter) =>
      parameterStatusUtils.simplifyStatus(
        parameterStatusUtils.calculateStatus(
          parameter.value,
          parameter.range.min,
          parameter.range.max,
          parameter.date,
        ).status,
      ) === 'warning',
  ).length;
  const criticalMarkers = aggregatedParameters.filter(
    (parameter) =>
      parameterStatusUtils.simplifyStatus(
        parameterStatusUtils.calculateStatus(
          parameter.value,
          parameter.range.min,
          parameter.range.max,
          parameter.date,
        ).status,
      ) === 'critical',
  ).length;
  const outdatedMarkers = aggregatedParameters.filter(
    (parameter) =>
      parameterStatusUtils.simplifyStatus(
        parameterStatusUtils.calculateStatus(
          parameter.value,
          parameter.range.min,
          parameter.range.max,
          parameter.date,
        ).status,
      ) === 'outdated',
  ).length;

  const filteredData = data?.filter((data: ParameterData) => data.name !== "bas") ?? [];

  useEffect(() => {
    void sendEvent({
      event: EventType.PAGE_VIEW,
      sub_category: "parameters_page",
    })
  }, [])

  if (isLoading) {
    return (
      <div className="relative w-full h-full">
        <LoadingOverlay />
      </div>
    )
  }

  if (isError) {
    return <div>Error</div>;
  }

  return (
    <div className='w-full h-full space-y-lg'>
      <OverviewCard
        numberOfMarkers={numberOfMarkers}
        normalMarkers={normalMarkers}
        warningMarkers={warningMarkers}
        criticalMarkers={criticalMarkers}
        outdatedMarkers={outdatedMarkers}
      />
      <FilterBar
        onFilterChange={setIsFilteringCategory}
        isFilteringCategory={isFilteringCategory}
      />
      {isFilteringCategory ? (
        <CategoryHealthTrend data={filteredData} />
      ) : (
        <RiskHealthTrend data={filteredData} />
      )}
    </div>
  );
};

export default Parameters;
